import { clsx } from "clsx";
import React from "react";
import { Placement, Tooltip } from "../Tooltip";
import "./StatusChip.css";

/**
 * StatusChip variant types
 * Represents the visual style of the status chip
 */
export type StatusChipVariant =
  | "todo"
  | "done"
  | "error"
  | "final"
  | "processing";

/**
 * StatusChip dot variant types
 * Used with mode="dot" — a subtle chip with a colored dot indicator
 */
export type StatusChipDotVariant =
  | "neutral"
  | "info"
  | "danger"
  | "success"
  | "warning";

/**
 * StatusChip display mode
 */
export type StatusChipMode = "filled" | "dot";

/**
 * StatusChip size types
 * Defines the size and padding of the status chip
 */
export type StatusChipSize = "x-small" | "small" | "medium" | "large";

/**
 * StatusChip props
 */
export interface StatusChipProps {
  /**
   * Text content to display in the chip
   */
  label: string;

  /**
   * Display mode: "filled" (default) or "dot" (subtle chip with colored dot indicator)
   */
  mode?: StatusChipMode;

  /**
   * Color variant — applies to "filled" mode (default: 'todo')
   */
  variant?: StatusChipVariant;

  /**
   * Dot color variant — applies when mode="dot" (default: 'neutral')
   */
  dotVariant?: StatusChipDotVariant;

  /**
   * Size of the status chip
   */
  size?: StatusChipSize;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Optional tooltip content shown on hover/focus
   */
  tooltipContent?: React.ReactNode;

  /**
   * Tooltip placement relative to the chip
   */
  tooltipPlacement?: Placement;

  /**
   * Test ID for testing
   */
  dataTestId?: string;
}

/**
 * StatusChip component
 *
 * A visual indicator for status information, displayed as a colored chip/badge.
 * Used to represent status, state, or category in a compact visual format.
 *
 * @example
 * ```tsx
 * <StatusChip
 *   label="Completed"
 *   variant="done"
 *   size="medium"
 * />
 * ```
 */
export const StatusChip: React.FC<StatusChipProps> = ({
  label,
  mode = "filled",
  variant = "todo",
  dotVariant = "neutral",
  size = "medium",
  className = "",
  tooltipContent,
  tooltipPlacement = "top",
  dataTestId,
}) => {
  const isDot = mode === "dot";

  const chip = (
    <span
      className={clsx(
        "indigo-status-chip",
        isDot ? `mode-dot variant-${dotVariant}` : `variant-${variant}`,
        `size-${size}`,
        className,
      )}
      data-testid={dataTestId}
    >
      {isDot && <span className="indigo-status-chip-dot" aria-hidden="true" />}
      {label}
    </span>
  );

  if (!tooltipContent) {
    return chip;
  }

  return (
    <Tooltip content={tooltipContent} placement={tooltipPlacement}>
      {chip}
    </Tooltip>
  );
};

export default StatusChip;
